/**
 * ProvePhoneBanner — banner-center source (Prest0n Everywhere S1c-1b-2; F-16, Miles 2026-10-22: "prompt once").
 *
 * A person whose number was confirmed only by a YES reply to a text is told once, before that stops counting (S1c-2),
 * and pointed to Settings → "Phones & email": the action leads to the section's address (PROVE_PHONE_TO), where the
 * page lands on the add-a-phone field once the section shows (S1c-1b-3). The rule is provePhoneBannerPolicy.ts and the
 * words provePhoneCopy.ts; this is the wiring:
 *
 *   - who: the signed-in person (not anonymous), in the tab's workspace (resolveTabWorkspace, as Settings resolves it),
 *     where the client lever admits it, with an SMS consent record that says confirmed;
 *   - the number: the list door's `provePhone`, asked once per person and workspace per page load, and only once all
 *     of the above hold; its client loads on demand, in its own chunk (identifiers-client, held off the first
 *     download by check-chat-first-paint R4), since the band is on the first download. Never asked on Settings,
 *     which reads the list itself and says the same thing beside the add-a-phone form. A list that named no number is
 *     not asked again for a day, for that person, workspace and YES (provePhoneQuietKey); each answer is kept for its
 *     own person and workspace, whatever page the person is on when it lands;
 *   - seen for good, per person: the X, or reaching Settings once the number is known (its action leads there), marks
 *     it, under a hash of the person's id (provePhoneSeenKey), never the id itself.
 */

import React, { useContext, useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { useAuth } from '../../context/AuthContext';
import { useBannerSurface } from './bannerSurface';
import { BannerBusContext, useRegisterBanner, type BannerDescriptor } from './GlobalBannerCenter';
import type { BannerNotice } from './bannerNotice';
import { subscribeSmsConsent, type SmsConsentState } from '../../services/smsVerificationClient';
import { accountSecurityShows } from '../../services/identity/accountSecurityLever';
import { resolveTabWorkspace } from '../../services/prest0nRuntime/tabWorkspace';
import { appPathname } from '../../design/previewPath';
import { PROVE_PHONE_COPY as WORDS } from './provePhoneCopy';
import {
  PROVE_PHONE_TO,
  isSettingsPath,
  provePhoneShows,
  provePhoneWorthReading,
  readProvePhoneQuiet,
  readProvePhoneSeen,
  writeProvePhoneQuiet,
  writeProvePhoneSeen,
} from './provePhoneBannerPolicy';

const ActionLink = styled(Link)`
  flex: none;
  padding: 4px 14px;
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 700;
  color: #fff;
  background: #8a6b2a;
  text-decoration: none;
  transition: filter 0.15s ease;
  &:hover { filter: brightness(1.08); }
`;

function localStore(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    return null;
  }
}

export const ProvePhoneBanner: React.FC = () => {
  const bus = useContext(BannerBusContext);
  const onAppSurface = useBannerSurface();
  const { pathname } = useLocation();
  const onSettings = isSettingsPath(appPathname(pathname));
  const { user } = useAuth();

  const [uid, setUid] = useState<string | null>(null);
  const [consent, setConsent] = useState<SmsConsentState>({ status: 'none', phoneMasked: null });
  // The list door's answers, one per question (below): the number, or null (none, or the door refused). Kept by key,
  // so an older question's answer landing last never takes the place of the current one (review round 2, L1).
  const [reads, setReads] = useState<Record<string, { display: string } | null>>({});
  const [seenBy, setSeenBy] = useState<Record<string, boolean>>({});
  const askedRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    const unsub = onAuthStateChanged(getAuth(), (u) => setUid(u && !u.isAnonymous ? u.uid : null));
    return unsub;
  }, []);

  useEffect(() => {
    if (!uid) {
      setConsent({ status: 'none', phoneMasked: null });
      return undefined;
    }
    return subscribeSmsConsent(uid, setConsent);
  }, [uid]);

  const accountId = uid ? resolveTabWorkspace({ uid, accountFromPointer: user?.accountId }).accountId : null;
  // One question per person, workspace and YES confirmation: a number confirmed while the page is open is asked about,
  // and its answer kept apart from the last one's (review round 3, N2).
  const key = uid && accountId ? `${uid}:${accountId}:${consent.confirmedAtMs ?? ''}` : null;
  const seen = !!uid && (seenBy[uid] ?? readProvePhoneSeen(uid, localStore()));
  const facts = { uid, leverAdmits: !!accountId && accountSecurityShows(accountId), consent: consent.status, seen };
  const worth = provePhoneWorthReading(facts);
  const provePhone = key ? reads[key] ?? null : null;

  useEffect(() => {
    if (!worth || onSettings || !uid || !key || !accountId || askedRef.current.has(key)) return;
    askedRef.current.add(key);
    const question = { uid, accountId, confirmedAtMs: consent.confirmedAtMs ?? null };
    if (readProvePhoneQuiet(question, localStore(), Date.now())) return;
    void (async () => {
      let answer: { display: string } | null = null;
      try {
        const { identifiersClient } = await import(/* webpackChunkName: "identifiers-client" */ '../../services/identity/identifiersClient');
        const doors = identifiersClient({ refreshToken: async () => { await getAuth().currentUser?.getIdToken(true); } });
        const listed = await doors.list(accountId);
        if (listed.ok) {
          answer = listed.value.provePhone;
          // "None" is remembered; "none this time" (the consent record unread, round 2, L2) is asked again.
          if (!answer && !listed.value.provePhoneUnread) writeProvePhoneQuiet(question, localStore(), Date.now());
        }
      } catch {
        answer = null;
      }
      // Kept whatever page the person is on by now (review round 1, L1), under its own key: it is used only for its own
      // question, and each question is read once per page load.
      setReads((r) => ({ ...r, [key]: answer }));
    })();
  }, [worth, onSettings, uid, key, accountId, consent.confirmedAtMs]);

  const markSeen = () => {
    if (!uid) return;
    writeProvePhoneSeen(uid, localStore());
    setSeenBy((s) => ({ ...s, [uid]: true }));
  };

  // Reaching Settings once the number is known is the notice seen: its action leads there, and Settings says the same.
  useEffect(() => {
    if (onSettings && worth && provePhone) markSeen();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onSettings, worth, provePhone]);

  const eligible = provePhoneShows({ ...facts, provePhone, onSettings, onAppSurface });
  const words = provePhone ? WORDS.words(provePhone.display) : '';
  const notice: BannerNotice = {
    words,
    shortWords: WORDS.wordsShort,
    action: { label: WORDS.action, to: PROVE_PHONE_TO },
  };

  const descriptor: BannerDescriptor | null = eligible
    ? {
        id: 'prove-phone',
        tone: 'info',
        dismissible: true,
        onDismiss: markSeen,
        notice,
        shortContent: (
          <>
            <span>{WORDS.wordsShort}</span>
            <ActionLink to={PROVE_PHONE_TO}>{WORDS.action}</ActionLink>
          </>
        ),
        content: (
          <>
            <span>{words}</span>
            <ActionLink to={PROVE_PHONE_TO}>{WORDS.action}</ActionLink>
          </>
        ),
      }
    : null;

  useRegisterBanner(bus ? descriptor : null);

  // Banner-center-native: outside the center this source renders nothing.
  return null;
};

export default ProvePhoneBanner;
